/**
 * The orthogonal shadow box over one depth range of the viewing camera, fitted as Godot's
 * `_light_instance_setup_directional_shadow` fits each split (`renderer_scene_cull.cpp:2134-2353`).
 * Pure: it reads THREE maths and writes nothing.
 * `directionalShadow.md` beside this file has the port and its divergences.
 */
import * as THREE from 'three';
import { snapped } from '../../godot/math/math.js';
import { directionalShadowSlice, directionalShadowSnapStep, directionalShadowTexelSize, pancakesCasters, texelPaddedRadius, } from '../../godot/rendering/lights/directionalShadow.js';
import { softShadowExpand } from '../../godot/rendering/lights/directionalSoftShadow.js';
/** A bare `THREE.Camera` has no depth range. */
export function isViewingCamera(camera) {
    const candidate = camera;
    return typeof candidate.near === 'number' && typeof candidate.far === 'number';
}
/**
 * Godot flattens a caster nearer the light than the near plane onto it
 * (`scene_forward_clustered.glsl:679-682`), so every such caster casts. three has no pancaking,
 * so the near plane moves this many diameters of the whole view slice's sphere further towards
 * the light. Every split takes the same reach, so a caster casts into all of them or none.
 */
const CASTER_REACH_IN_DIAMETERS = 1;
const NDC_CORNERS = [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
];
const CORNER_COUNT = 2 * NDC_CORNERS.length;
/**
 * Scratch for the fit, which runs on every render, so it allocates no vector per box. Each value
 * lasts until the next box or reach is fitted. No fitted box keeps one.
 */
const scratchCorners = Array.from({ length: CORNER_COUNT }, () => new THREE.Vector3());
const scratchOnNearPlane = new THREE.Vector3();
const scratchOnFarPlane = new THREE.Vector3();
const scratchMean = new THREE.Vector3();
const scratchSphere = new THREE.Sphere();
const scratchBasis = new THREE.Matrix4();
/** The camera depths the light's shadow covers (`renderer_scene_cull.cpp:2143-2149`). */
export function viewSlice(input) {
    const { camera, declaration } = input;
    return directionalShadowSlice(camera.near, camera.far, declaration.maxDistance, camera.isOrthographicCamera === true);
}
/**
 * Fits boxes for one light and one render. The light's axes and its caster reach hold for every
 * split, so this computes them once, and each split passes only its own depths. A box is null when
 * the inputs give no finite one, such as a camera with a non-invertible projection. The caller then
 * leaves the light's shadow as it is.
 */
export function directionalShadowBoxFitter(input) {
    const axes = lightAxes(input.lightPosition, input.targetPosition, input.up);
    const reach = pancakesCasters(input.declaration.pancakeSize) ? casterReach(input, axes) : null;
    const frame = { input, axes, reach };
    return (depths) => fitBox(frame, depths);
}
function fitBox({ input, axes, reach }, depths) {
    const { camera, declaration, shadowMapSize } = input;
    const { center: centre, radius } = paddedSphere(writeCameraSliceCorners(camera, depths.near, depths.far, scratchCorners), shadowMapSize);
    const centreX = axes.x.dot(centre);
    const centreY = axes.y.dot(centre);
    const centreZ = axes.z.dot(centre);
    // `renderer_scene_cull.cpp:2284` and `:2327`: the far side sits one radius past the centre,
    // the near side one radius plus the pancake towards the light.
    const zFar = centreZ - radius;
    const zNear = centreZ + radius + declaration.pancakeSize;
    const halfWidth = radius + softShadowExpand(declaration.softShadowAngle, zNear - zFar);
    const step = directionalShadowSnapStep(halfWidth, shadowMapSize);
    const xMin = snapped(centreX - halfWidth, step);
    const xMax = snapped(centreX + halfWidth, step);
    const yMin = snapped(centreY - halfWidth, step);
    const yMax = snapped(centreY + halfWidth, step);
    const zNearCovered = reach === null ? zNear : Math.max(zNear, reach);
    const eyeX = axes.x.dot(input.lightPosition);
    const eyeY = axes.y.dot(input.lightPosition);
    const eyeZ = axes.z.dot(input.lightPosition);
    const near = eyeZ - zNearCovered;
    const far = eyeZ - zFar;
    const box = {
        left: xMin - eyeX,
        right: xMax - eyeX,
        bottom: yMin - eyeY,
        top: yMax - eyeY,
        near,
        far,
        // `renderer_scene_cull.cpp:2348` spends the bias over Godot's own depth range.
        bias: declaration.depthBias * ((zNear - zFar) / (far - near)),
        normalBias: declaration.normalBias * directionalShadowTexelSize(radius, shadowMapSize),
        rangeBegin: zNear,
    };
    return isFiniteBox(box) ? box : null;
}
/** Field by field, so the check builds no array on every render. */
function isFiniteBox(box) {
    return (Number.isFinite(box.left) &&
        Number.isFinite(box.right) &&
        Number.isFinite(box.top) &&
        Number.isFinite(box.bottom) &&
        Number.isFinite(box.near) &&
        Number.isFinite(box.far) &&
        Number.isFinite(box.bias) &&
        Number.isFinite(box.normalBias) &&
        Number.isFinite(box.rangeBegin));
}
/**
 * The slice corners' sphere (`renderer_scene_cull.cpp:2268-2282`), padded by one texel. It is
 * the shared scratch sphere, valid until the next call.
 */
function paddedSphere(corners, shadowMapSize) {
    const sphere = meanCentredSphere(corners);
    sphere.radius = texelPaddedRadius(sphere.radius, shadowMapSize);
    return sphere;
}
/**
 * How far towards the light, along `axes.z`, the near plane reaches: one diameter past the whole
 * view slice's own near face, whatever depths the box itself covers.
 */
function casterReach(input, axes) {
    const slice = viewSlice(input);
    const { center: centre, radius } = paddedSphere(writeCameraSliceCorners(input.camera, slice.near, slice.far, scratchCorners), input.shadowMapSize);
    const sliceNearFace = axes.z.dot(centre) + radius + input.declaration.pancakeSize;
    return sliceNearFace + CASTER_REACH_IN_DIAMETERS * 2 * radius;
}
/**
 * Writes the eight world-space corners of the camera's view between two depths
 * (`Projection::get_endpoints`, called at `renderer_scene_cull.cpp:2206`) into `corners`, near then
 * far for each corner ray, and returns it. Each ray is unprojected once and cut at a depth, which
 * holds for a perspective and an orthogonal camera.
 */
function writeCameraSliceCorners(camera, nearDepth, farDepth, corners) {
    NDC_CORNERS.forEach(([x, y], ray) => {
        scratchOnNearPlane.set(x, y, -1).applyMatrix4(camera.projectionMatrixInverse);
        scratchOnFarPlane.set(x, y, 1).applyMatrix4(camera.projectionMatrixInverse);
        writeCornerAtDepth(camera, nearDepth, corners[2 * ray]);
        writeCornerAtDepth(camera, farDepth, corners[2 * ray + 1]);
    });
    return corners;
}
/** Cuts the ray through the two scratch plane points at `depth`, in world space. */
function writeCornerAtDepth(camera, depth, corner) {
    pointAtDepth(scratchOnNearPlane, scratchOnFarPlane, depth, corner).applyMatrix4(camera.matrixWorld);
}
/**
 * The sphere round the points' mean (`renderer_scene_cull.cpp:2268-2280`). Without a centre,
 * `Sphere.setFromPoints` centres on the bounding box and so fits a different radius. It is the
 * shared scratch sphere, valid until the next call.
 */
function meanCentredSphere(points) {
    scratchMean.set(0, 0, 0);
    for (const point of points)
        scratchMean.add(point);
    return scratchSphere.setFromPoints(points, scratchMean.divideScalar(points.length));
}
/**
 * Writes into `target` the point on the view-space line through `a` and `b` whose depth, along -Z,
 * is `depth`, and returns it.
 */
function pointAtDepth(a, b, depth, target) {
    const t = (-depth - a.z) / (b.z - a.z);
    return target.copy(a).lerp(b, t);
}
/**
 * Built by the same `Matrix4.lookAt` three's `LightShadow.updateMatrices` runs through
 * `Object3D.lookAt`, so a box fitted on these axes is the box three draws. A direction parallel
 * to `up` takes three's own nudge, and the box stays square to it. The fitter keeps them for
 * every split, so they are its own vectors.
 */
function lightAxes(lightPosition, targetPosition, up) {
    const axes = { x: new THREE.Vector3(), y: new THREE.Vector3(), z: new THREE.Vector3() };
    scratchBasis.lookAt(lightPosition, targetPosition, up);
    scratchBasis.extractBasis(axes.x, axes.y, axes.z);
    return axes;
}
//# sourceMappingURL=fitDirectionalShadowBox.js.map